import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import PersonIcon from '@material-ui/icons/Person';
import { getSessionCapacity } from 'app/store/actions/sessions.actions';

const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        alignItems: 'center',
        marginLeft: 'auto',
        marginRight: theme.spacing(2),
        padding: theme.spacing(0.5, 1),
        borderRadius: theme.shape.borderRadius,
        cursor: 'default'
    },
    rootLight: {
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary
    },
    rootDark: {
        backgroundColor: '#1e293b',
        color: '#f8fafc',
        border: '1px solid #334155'
    },
    icon: {
        fontSize: 20,
        marginRight: theme.spacing(0.5)
    },
    iconLight: {
        color: theme.palette.primary.main
    },
    iconDark: {
        color: '#60a5fa'
    },
    text: {
        fontSize: '1.2rem',
        fontWeight: 500
    },
    textLight: {
        color: 'inherit'
    },
    textDark: {
        color: '#f8fafc'
    },
    warning: {
        color: '#fbbf24'
    },
    error: {
        color: '#ef4444'
    }
}));

function UserCapacityInfo() {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { capacity } = useSelector(({ sessions }) => sessions);
    const themeMode = useSelector(({ modernDashboard }) => modernDashboard?.userPreferences?.themeMode || 'dark');
    
    useEffect(() => {
        dispatch(getSessionCapacity());
    }, [dispatch]);
    
    // Don't render anything if we don't have capacity data yet
    if (!capacity || !capacity.maxConcurrentUsers) {
        return null;
    }
    
    const { totalActiveUsers, maxConcurrentUsers, capacityReached } = capacity;
    
    // Determine styling based on theme mode and status
    const isDarkMode = themeMode === 'dark';
    const rootClass = `${classes.root} ${isDarkMode ? classes.rootDark : classes.rootLight}`;
    const iconClass = `${classes.icon} ${isDarkMode ? classes.iconDark : classes.iconLight}`;
    
    let textClass = `${classes.text} ${isDarkMode ? classes.textDark : classes.textLight}`;
    if (capacityReached) {
        textClass = `${textClass} ${classes.error}`;
    } else if (maxConcurrentUsers !== 'unlimited' && totalActiveUsers >= maxConcurrentUsers * 0.8) {
        textClass = `${textClass} ${classes.warning}`;
    }
    
    const tooltipContent = capacityReached 
        ? "License capacity reached. New users may not be able to log in."
        : `Total capacity: ${maxConcurrentUsers}`;
    
    return (
        <Tooltip title={tooltipContent}>
            <div className={rootClass}>
                <PersonIcon className={iconClass} />
                <Typography className={textClass}>
                    {totalActiveUsers} Active Users
                </Typography>
            </div>
        </Tooltip>
    );
}

export default UserCapacityInfo;
